/**
 * Future Demand pricing engine — the single source of truth for what we sell.
 *
 * All plan subtitles, family taglines, prices, and the Affinity included-request
 * count are sourced from `./website-catalog.json`, which is generated from the
 * marketing site (fd-website: src/data/apps.ts + the three pricing Astro pages)
 * by `scripts/sync-pricing-from-website.mjs`. Do not hand-edit the JSON; re-run
 * `pnpm sync:catalog` to refresh.
 *
 * Families: Live (apps × volume × interval × currency → plan), Prisma (3 tiers),
 * Affinity (annual base + metered overage). Terms are annual or quarterly,
 * prepaid — never monthly.
 */

import bundledCatalog from "./website-catalog.json";
import { LIVE_PRO360_25K_INDEX, VOLUME_TIERS, VOLUME_LABELS, VOLUME_TIER_ORDER } from "./live-volume";
export * from "./live-volume";
import {
  DATA_INTEGRATION_ADDON_DEFAULT_NAME,
  DATA_INTEGRATION_ADDON_DEFAULT_SUBTITLE,
  type AffinityDataIntegrationAddon,
  type ProductFields,
} from "./catalog";

export type Currency = "EUR" | "USD";
export type Interval = "annual" | "quarterly";
export type AppCode = "lookout" | "wave" | "backhaul" | "sentinel";
export type Family = "live" | "prisma" | "affinity";

/**
 * The catalog shape — the structure of website-catalog.json. The bundled JSON is the
 * build-time default; setCatalog() swaps in the live PUBLISHED snapshot at runtime.
 */
export type Catalog = typeof bundledCatalog;

/** The build-time bundled snapshot — the fallback when no live catalog is hydrated. */
export const BUNDLED_CATALOG: Catalog = bundledCatalog;

// The catalog the engine prices against. Defaults to the bundled snapshot so the engine
// works with zero setup (tests, node scripts, the first paint before hydration). The SPAs
// fetch /api/public/catalog and call setCatalog() before first render; the API hydrates it
// from the published D1 snapshot per pricing request. Everything reads ONE source — the
// current published catalog — so a catalog publish goes live with no rebuild/redeploy.
let active: Catalog = bundledCatalog;

/**
 * Swap the active catalog. The derived marketing-metadata exports (APP_NAMES,
 * APP_DESCRIPTIONS, FAMILY_META) are recomputed so importers observe the new values via
 * ESM live bindings; engine functions read `active` directly and pick it up immediately.
 */
export function setCatalog(next: Catalog): void {
  active = next;
  APP_NAMES = computeAppNames();
  APP_DESCRIPTIONS = computeAppDescriptions();
  FAMILY_META = computeFamilyMeta();
}

/** The catalog the engine is currently pricing against. */
export function getActiveCatalog(): Catalog {
  return active;
}

/** Reset to the build-time bundled catalog (e.g. if a live fetch fails). */
export function resetCatalog(): void {
  setCatalog(bundledCatalog);
}

export const CURRENCIES: Currency[] = ["EUR", "USD"];
export const LIVE_APPS: AppCode[] = ["lookout", "wave", "backhaul", "sentinel"];

function computeAppNames(): Record<AppCode, string> {
  return {
    lookout: active.apps.lookout.name,
    wave: active.apps.wave.name,
    backhaul: active.apps.backhaul.name,
    sentinel: active.apps.sentinel.name,
  };
}
export let APP_NAMES: Record<AppCode, string> = computeAppNames();

// ── Helpers ──────────────────────────────────────────────────────────────────
const sym: Record<Currency, string> = { EUR: "€", USD: "$" };

/** Total prepaid for one term, from the monthly-equivalent rate. */
export function billedTotal(monthly: number, interval: Interval): number {
  return monthly * (interval === "annual" ? 12 : 3);
}

export function fmtMoney(amount: number, currency: Currency): string {
  return `${sym[currency]}${amount.toLocaleString("en-US")}`;
}

/** "€890/mo" */
export function fmtMonthly(monthly: number, currency: Currency): string {
  return `${fmtMoney(monthly, currency)}/mo`;
}

export const INTERVAL_LABEL: Record<Interval, string> = { annual: "Annual", quarterly: "Quarterly" };
export const billingNote = (interval: Interval): string =>
  interval === "annual" ? "billed annually, prepaid" : "billed quarterly, prepaid";

// ── Live ─────────────────────────────────────────────────────────────────────
export interface LivePlanResult {
  key: "essentials" | "core" | "proGrowth" | "proEngage" | "pro360";
  name: string;
  subtitle: string;
  apps: AppCode[];
  /** Monthly-equivalent for the chosen interval; null = contact us / unavailable. */
  monthly: number | null;
  annualMonthly: number | null;
  quarterlyMonthly: number | null;
  recommended: boolean;
}
export interface ScoutPlanResult {
  key: "scout" | "scoutPro" | "scoutEnt";
  name: string;
  subtitle: string;
  monthly: number; // annual term
  recommended: boolean;
}
export interface LiveQuote {
  mode: "standard" | "scout";
  interval: Interval;
  currency: Currency;
  volumeIndex: number;
  apps: AppCode[];
  plans: LivePlanResult[];
  scout?: { plans: ScoutPlanResult[]; scenarioValidation: number };
  warning?: string;
}

const LIVE_KEYS = ["essentials", "core", "proGrowth", "proEngage", "pro360"] as const;
type LiveKey = (typeof LIVE_KEYS)[number];

function livePlanFromCatalog(key: LiveKey, currency: Currency, vi: number): {
  apps: AppCode[];
  name: string;
  subtitle: string;
  annualMonthly: number | null;
  quarterlyMonthly: number | null;
} {
  const p = active.live[key];
  const priceTable = p[currency];
  return {
    apps: p.apps as AppCode[],
    name: p.name,
    subtitle: p.subtitle,
    annualMonthly: priceTable.annual[vi] ?? null,
    quarterlyMonthly: priceTable.quarterly ? (priceTable.quarterly[vi] ?? null) : null,
  };
}

export function isScoutMode(apps: AppCode[]): boolean {
  return apps.includes("lookout") && !apps.includes("wave") && !apps.includes("backhaul") && !apps.includes("sentinel");
}

export function liveQuote(input: {
  apps: AppCode[];
  volumeIndex: number;
  interval: Interval;
  currency: Currency;
}): LiveQuote {
  const { apps, volumeIndex: vi, interval, currency } = input;
  const base = { mode: "standard" as const, interval, currency, volumeIndex: vi, apps, plans: [] as LivePlanResult[] };

  if (apps.length === 0) return { ...base, warning: "Select at least one app." };

  // Scout mode — Lookout-only forecasting tier set.
  if (isScoutMode(apps)) {
    const s = active.live.scout;
    const sv = active.live.scenarioValidation;
    const scoutPlans: ScoutPlanResult[] = (["scout", "scoutPro", "scoutEnt"] as const).map((k) => ({
      key: k,
      name: s[k].name,
      subtitle: s[k].subtitle,
      monthly: s[k][currency],
      recommended: s[k].recommended,
    }));
    return {
      mode: "scout",
      interval,
      currency,
      volumeIndex: vi,
      apps,
      plans: [],
      scout: { plans: scoutPlans, scenarioValidation: sv[currency] },
    };
  }

  const hC = apps.includes("lookout") || apps.includes("wave");
  const hR = apps.includes("backhaul") || apps.includes("sentinel");
  const out: LivePlanResult[] = [];
  for (const key of LIVE_KEYS) {
    if (vi === LIVE_PRO360_25K_INDEX && key !== "pro360") continue;
    const def = livePlanFromCatalog(key, currency, vi);
    if (!apps.every((a) => def.apps.includes(a))) continue; // plan must cover all selected apps
    if (key === "essentials" && vi > 0) continue;
    if (hC && hR && (key === "proGrowth" || key === "proEngage")) continue;
    if (!hR && key === "proEngage") continue;
    if (!hC && (key === "proGrowth" || key === "essentials" || key === "core")) continue;
    if (interval === "quarterly" && def.quarterlyMonthly === null) continue;
    out.push({
      key,
      name: def.name,
      subtitle: def.subtitle,
      apps: def.apps,
      monthly: interval === "quarterly" ? def.quarterlyMonthly : def.annualMonthly,
      annualMonthly: def.annualMonthly,
      quarterlyMonthly: def.quarterlyMonthly,
      recommended: false,
    });
  }

  // Recommended (mirror the site's rk()).
  let recKey: LivePlanResult["key"];
  if (hC && hR) recKey = "pro360";
  else if (hR && !hC) recKey = "proEngage";
  else recKey = out.some((r) => r.key === "core") ? "core" : "proGrowth";
  for (const r of out) r.recommended = r.key === recKey;

  return {
    ...base,
    plans: out,
    warning: out.length === 0 ? (interval === "quarterly" ? "No quarterly plan at this volume — try annual." : "No plan for this combination.") : undefined,
  };
}

// ── Prisma ───────────────────────────────────────────────────────────────────
export interface PrismaPlanResult {
  key: "essentials" | "core" | "coreplus";
  name: string;
  subtitle: string;
  monthly: number | null;
  annualOnly: boolean;
  recommended: boolean;
}
const PRISMA_RECOMMENDED: Record<"essentials" | "core" | "coreplus", boolean> = {
  essentials: false,
  core: true,
  coreplus: false,
};
export function prismaQuote(currency: Currency, interval: Interval): PrismaPlanResult[] {
  return (["essentials", "core", "coreplus"] as const).map((key) => {
    const p = active.prisma[key];
    const prices = p[currency];
    const annualOnly = prices.quarterly === null;
    const monthly = interval === "quarterly" ? prices.quarterly : prices.annual;
    return {
      key,
      name: p.name,
      subtitle: p.subtitle,
      monthly,
      annualOnly,
      recommended: PRISMA_RECOMMENDED[key],
    };
  });
}

// ── Affinity (annual base + metered overage) ─────────────────────────────────
export interface AffinityPlanResult {
  key: "core" | "pro";
  name: string;
  subtitle: string;
  monthly: number; // annual term
  includedRequests: number;
  recommended: boolean;
}
export interface AffinityQuote {
  plans: AffinityPlanResult[];
  dataIntegrationAddon: {
    name: string;
    subtitle: string;
    monthly: number;
  };
  /** @deprecated Prefer dataIntegrationAddon.monthly. */
  dataIntegrationAddonMonthly: number;
  includedRequests: number;
  /** Overage rate per additional 100k requests, by monthly band. */
  volumeBands: Array<{ band: string; per100k: number }>;
}

/**
 * Read the add-on copy from the live catalog. The defaults cover published
 * snapshots created before name/subtitle became part of the snapshot shape.
 */
function activeDataIntegrationAddon(): AffinityDataIntegrationAddon {
  const addon = active.affinity.dataIntegrationAddon as Partial<AffinityDataIntegrationAddon> &
    Pick<AffinityDataIntegrationAddon, "EUR" | "USD">;
  return {
    name: addon.name ?? DATA_INTEGRATION_ADDON_DEFAULT_NAME,
    subtitle: addon.subtitle ?? DATA_INTEGRATION_ADDON_DEFAULT_SUBTITLE,
    EUR: addon.EUR,
    USD: addon.USD,
  };
}

export function affinityQuote(currency: Currency): AffinityQuote {
  const a = active.affinity;
  const addon = activeDataIntegrationAddon();
  return {
    plans: [
      { key: "core", name: a.core.name, subtitle: a.core.subtitle, monthly: a.core[currency], includedRequests: a.includedRequests, recommended: false },
      { key: "pro", name: a.pro.name, subtitle: a.pro.subtitle, monthly: a.pro[currency], includedRequests: a.includedRequests, recommended: true },
    ],
    dataIntegrationAddon: {
      name: addon.name,
      subtitle: addon.subtitle,
      monthly: addon[currency],
    },
    dataIntegrationAddonMonthly: addon[currency],
    includedRequests: a.includedRequests,
    volumeBands: Object.entries(a.volumeRates).map(([band, rate]) => ({ band, per100k: rate[currency] })),
  };
}

// ── Add-ons ─────────────────────────────────────────────────────────────────
export const ADD_ON_KEYS = [
  "ad_spend_package",
  "prisma_core_additional_user",
  "prisma_coreplus_additional_user",
  "credit_package",
] as const;
export type AddOnKey = (typeof ADD_ON_KEYS)[number];
export const AD_SPEND_PACKAGE_ADDON_KEY = "ad_spend_package" as const;
export const AD_SPEND_MANAGEMENT_FEE_PRODUCT_KEY = "ad_spend_management_fee" as const;
export const PRISMA_CORE_USER_ADDON_KEY = "prisma_core_additional_user" as const;
export const PRISMA_COREPLUS_USER_ADDON_KEY = "prisma_coreplus_additional_user" as const;
export const CREDIT_PACKAGE_ADDON_KEY = "credit_package" as const;

/** Commercial identity of the self-serve ad-spend offer. Clients select the
 * campaign budget in exact EUR 1,000 steps; Future Demand invoices that funding
 * plus a separately itemised 5% management fee. */
export const AD_SPEND_OFFER_VERSION = "self_service_ad_spend_v1" as const;
export const AD_SPEND_MANAGEMENT_FEE_PERCENT = 5 as const;
export const AD_SPEND_MANAGEMENT_FEE_BPS = 500 as const;
export const AD_SPEND_PACKAGE_INCREMENT_EUR = 1_000;
export const AD_SPEND_PACKAGE_MIN_EUR = 1_000;
// Keep the self-serve selector bounded; larger campaign budgets are handled as
// negotiated offers rather than an unbounded client-entered invoice.
export const AD_SPEND_PACKAGE_MAX_EUR = 999_000;

export interface AdSpendPackageOffer {
  version: typeof AD_SPEND_OFFER_VERSION;
  currency: "EUR";
  campaignBudgetEur: number;
  managementFeePercent: typeof AD_SPEND_MANAGEMENT_FEE_PERCENT;
  managementFeeEur: number;
  totalBeforeTaxEur: number;
  taxBehavior: "exclusive";
  requiresActiveSubscription: true;
  cashRedeemable: false;
  refundable: false;
}

/** Exact commercial offer shown before a self-serve ad-spend purchase. Selected
 * amounts are whole EUR 1,000 steps, so the 5% fee is always an exact euro value. */
export function adSpendPackageOffer(campaignBudgetEur: number): AdSpendPackageOffer {
  const managementFeeEur =
    (campaignBudgetEur * AD_SPEND_MANAGEMENT_FEE_BPS) / 10_000;
  return {
    version: AD_SPEND_OFFER_VERSION,
    currency: "EUR",
    campaignBudgetEur,
    managementFeePercent: AD_SPEND_MANAGEMENT_FEE_PERCENT,
    managementFeeEur,
    totalBeforeTaxEur: campaignBudgetEur + managementFeeEur,
    taxBehavior: "exclusive",
    requiresActiveSubscription: true,
    cashRedeemable: false,
    refundable: false,
  };
}

type AddonsCatalog = Catalog["addons"];
function activeAddons(): AddonsCatalog {
  // Published snapshots created before add-ons existed remain valid during rollout.
  return (active as Catalog & { addons?: AddonsCatalog }).addons ?? BUNDLED_CATALOG.addons;
}

export interface UserAddOnQuote {
  key: typeof PRISMA_CORE_USER_ADDON_KEY | typeof PRISMA_COREPLUS_USER_ADDON_KEY;
  name: string;
  subtitle: string;
  basePlanKey: "core" | "coreplus";
  monthlyPerUser: number;
}
export interface CreditPackageQuote {
  key: typeof CREDIT_PACKAGE_ADDON_KEY;
  name: string;
  subtitle: string;
  steps: Array<{ credits: number; amount: number; perThousand: number }>;
}
export interface AdSpendPackageQuote {
  key: typeof AD_SPEND_PACKAGE_ADDON_KEY;
  name: string;
  subtitle: string;
  currency: "EUR";
  increment: number;
  minimum: number;
  maximum: number;
  managementFeePercent: typeof AD_SPEND_MANAGEMENT_FEE_PERCENT;
  offerVersion: typeof AD_SPEND_OFFER_VERSION;
}
export interface AddOnQuote {
  adSpend: AdSpendPackageQuote;
  users: UserAddOnQuote[];
  credits: CreditPackageQuote;
}

export function addOnQuote(currency: Currency): AddOnQuote {
  const addons = activeAddons();
  const user = (
    key: UserAddOnQuote["key"],
    def: AddonsCatalog["prismaCoreAdditionalUser"],
    basePlanKey: UserAddOnQuote["basePlanKey"],
  ): UserAddOnQuote => ({
    key,
    name: def.name,
    subtitle: def.subtitle,
    basePlanKey,
    monthlyPerUser: def[currency],
  });
  return {
    adSpend: {
      key: AD_SPEND_PACKAGE_ADDON_KEY,
      name: "Prepaid campaign budget",
      subtitle: "Campaign budget plus a 5% management fee",
      currency: "EUR",
      increment: AD_SPEND_PACKAGE_INCREMENT_EUR,
      minimum: AD_SPEND_PACKAGE_MIN_EUR,
      maximum: AD_SPEND_PACKAGE_MAX_EUR,
      managementFeePercent: AD_SPEND_MANAGEMENT_FEE_PERCENT,
      offerVersion: AD_SPEND_OFFER_VERSION,
    },
    users: [
      user(PRISMA_CORE_USER_ADDON_KEY, addons.prismaCoreAdditionalUser, "core"),
      user(PRISMA_COREPLUS_USER_ADDON_KEY, addons.prismaCoreplusAdditionalUser, "coreplus"),
    ],
    credits: {
      key: CREDIT_PACKAGE_ADDON_KEY,
      name: addons.creditPackage.name,
      subtitle: addons.creditPackage.subtitle,
      steps: addons.creditPackage.steps.map((step) => ({
        credits: step.credits,
        amount: step[currency],
        perThousand: step[currency] / (step.credits / 1000),
      })),
    },
  };
}

/** Add-ons a contract can buy without Sales review. Credits are universal;
 * prepaid ad spend requires a Wave/Prisma contract; the additional-user SKU
 * follows the Prisma base plan on the contract. */
export function selfServiceAddOnKeys(
  lines: Array<{
    family: string | null;
    plan_key: string | null;
    product_code?: string | null;
    apps_json?: string | null;
    is_one_time?: number | boolean;
  }>,
): AddOnKey[] {
  const keys: AddOnKey[] = [];
  const managesAds = lines.some((line) => {
    if (line.is_one_time) return false;
    if (
      line.family === "prisma" &&
      (line.plan_key === "essentials" || line.plan_key === "core" || line.plan_key === "coreplus")
    ) {
      return true;
    }
    if (line.product_code === "wave" && !ADD_ON_KEYS.includes(line.plan_key as AddOnKey)) return true;
    if (!line.apps_json) return false;
    try {
      const apps = JSON.parse(line.apps_json) as unknown;
      return Array.isArray(apps) && apps.includes("wave");
    } catch {
      return false;
    }
  });
  if (managesAds) keys.push(AD_SPEND_PACKAGE_ADDON_KEY);
  if (lines.some((line) => line.family === "prisma" && line.plan_key === "core")) {
    keys.push(PRISMA_CORE_USER_ADDON_KEY);
  }
  if (lines.some((line) => line.family === "prisma" && line.plan_key === "coreplus")) {
    keys.push(PRISMA_COREPLUS_USER_ADDON_KEY);
  }
  keys.push(CREDIT_PACKAGE_ADDON_KEY);
  return keys;
}

/**
 * Family-level marketing metadata, sourced from the marketing site:
 *   tagline:     fd-website src/data/apps.ts → products.{live,prisma}.tagline
 *                (Affinity falls back to apps.affinity.tagline since the site's
 *                `products` export doesn't include it)
 *   description: fd-website src/data/apps.ts → products.{live,prisma}.description
 *                (Affinity uses apps.affinity.heroDescription)
 * Re-sync via `pnpm sync:catalog`.
 */
/** Family marketing meta. `de` carries the optional German overrides published
 * in the catalog (the customer overlay prefers it over the bundled translation);
 * canonical name/tagline/description stay English. */
export interface FamilyMeta {
  name: string;
  tagline: string;
  description: string;
  de?: { tagline?: string; description?: string };
}
function computeFamilyMeta(): Record<Family, FamilyMeta> {
  return {
    live: {
      name: "Live",
      tagline: active.products.live.tagline,
      description: active.products.live.description,
      de: (active.products.live as ProductFields).de,
    },
    prisma: {
      name: "Prisma",
      tagline: active.products.prisma.tagline,
      description: active.products.prisma.description,
      de: (active.products.prisma as ProductFields).de,
    },
    affinity: {
      name: "Affinity",
      tagline: active.products.affinity.tagline,
      description: active.products.affinity.description,
      de: (active.products.affinity as ProductFields).de,
    },
  };
}
export let FAMILY_META: Record<Family, FamilyMeta> = computeFamilyMeta();

/**
 * Per-app marketing descriptions, sourced from fd-website's app pages
 * (`src/data/apps.ts` → `apps.X.heroDescription`). Bundled inline (not fetched)
 * so offers stay useful when the marketing site is unreachable, and so the
 * wording is stable across snapshotted offers. Re-sync via `pnpm sync:catalog`.
 */
function computeAppDescriptions(): Record<AppCode, string> {
  return {
    lookout: active.apps.lookout.heroDescription,
    wave: active.apps.wave.heroDescription,
    backhaul: active.apps.backhaul.heroDescription,
    sentinel: active.apps.sentinel.heroDescription,
  };
}
export let APP_DESCRIPTIONS: Record<AppCode, string> = computeAppDescriptions();

/**
 * Annual ticket capacity each Live tier is sized for. Mirrors the
 * "Annual volume" slider on future-demand.com/pricing and the contractual
 * Volume definition in the Future Demand Order Terms v2 — see Schedule 2:
 *   "Volume" means the Customer's annual ticket capacity — the aggregate
 *   ticket capacity of all events (the seats made available across all
 *   venues, tours and channels, regardless of how many tickets are sold)
 *   together with equivalent audience Records — in the twelve-month period
 *   beginning on the Start Date. Where Customer exceeds the contracted
 *   Volume tier the next-higher tier price applies pro-rata from the date
 *   of exceedance.
 *
 * VOLUME_BLURB is the short paragraph we render under the slider and as a
 * tooltip on each "up to NNNk" label inside the offer.
 */
export const VOLUME_BLURB =
  "Annual ticket capacity — the total ticket capacity of your events (seats made available, not tickets sold) plus equivalent audience records, across all venues, tours and channels in the twelve months from the Start Date. Tier defined in Schedule 2 of the Future Demand Order Terms.";

const VOLUME_EXPLANATIONS: ReadonlyArray<string> = [
  "Up to 50,000 annual ticket capacity & equivalent audience records. Fits a single venue or one small tour.",
  "Up to 200,000 per year. Mid-size venue or a multi-date tour.",
  "Up to 300,000 per year.",
  "Up to 400,000 per year.",
  "Up to 500,000 per year.",
  "Up to 550,000 per year.",
  "Up to 600,000 per year.",
  "Up to 650,000 per year. Large promoter or arts organisation territory.",
  "700,000+ per year. Multi-market promoter — contact us so we can size correctly.",
  "800,000+ per year. Contact us.",
  "900,000+ per year. Contact us.",
  "1,000,000+ per year. National operator — bespoke terms.",
  "Up to 25,000 annual ticket capacity & equivalent audience records. Live Pro 360 includes 1,000 credits per month and 4 seats with a 24-month contract term.",
];

/** Long-form explanation for a Live volume tier index. */
export function volumeExplanation(volumeIndex: number): string {
  return VOLUME_EXPLANATIONS[volumeIndex] ?? VOLUME_BLURB;
}

/**
 * Essentials is the one Live plan whose included capacity is *tighter* than the
 * volume band it is sold under. It includes up to 25,000 in annual ticket
 * capacity — half the lowest "up to 50k" band — mirroring the "Up to 25k tickets/yr"
 * feature on future-demand.com/pricing → Live → Essentials. Every other Live
 * plan takes its capacity straight from the shared VOLUME_TIERS/VOLUME_LABELS,
 * so those bands must NOT be retuned to 25k (Core et al. genuinely go to 50k at
 * the lowest tier).
 */
export const ESSENTIALS_CAPACITY = 25000;
export const ESSENTIALS_CAPACITY_LABEL = "up to 25k";
export const ESSENTIALS_CAPACITY_EXPLANATION =
  "Up to 25,000 annual ticket capacity & equivalent audience records — the Essentials plan cap. Fits a single small venue or one short run.";

/**
 * Included-capacity label for a specific Live plan at a volume tier. Essentials
 * is pinned to its 25k cap; every other plan takes the shared volume-band label.
 * Use this — not `VOLUME_LABELS[vi]` directly — anywhere a *plan's* included
 * capacity is surfaced (offer lines, line notes), so Essentials never inherits
 * the 50k band it is priced under.
 */
export function liveCapacityLabel(planKey: string, volumeIndex: number): string {
  if (planKey === "essentials") return ESSENTIALS_CAPACITY_LABEL;
  if (planKey === "pro360" && volumeIndex === 0) return "25–50k";
  return VOLUME_LABELS[volumeIndex] ?? "";
}

/** Long-form capacity explanation for a specific Live plan — the Essentials cap
 * for Essentials, otherwise the shared volume-tier blurb. */
export function liveCapacityExplanation(planKey: string, volumeIndex: number): string {
  if (planKey === "essentials") return ESSENTIALS_CAPACITY_EXPLANATION;
  return volumeExplanation(volumeIndex);
}

// ── Offer-line builders ───────────────────────────────────────────────────────
/** Engine-shaped line ready to send to the offer API. Mirrors EngineLineInput. */
export interface EngineLineDraft {
  family: Family;
  plan_key: string;
  display_name: string;
  line_subtitle?: string;
  unit_amount_minor: number; // monthly-equivalent, in minor units (cents)
  currency_at_quote: Currency;
  interval_at_quote: Interval;
  volume_index?: number;
  apps?: AppCode[];
  is_one_time?: boolean;
  quantity?: number;
  product_code?: "lookout" | "wave" | "backhaul" | "sentinel" | "affinity_api";
  metadata?: Record<string, unknown>;
}

export const CUSTOMER_PRICE_SOURCES = [
  "stripe_subscription",
  "past_offer",
  "current_catalog",
  "legacy_price",
  "custom_agreement",
] as const;
export type CustomerPriceSource = (typeof CUSTOMER_PRICE_SOURCES)[number];
export type CustomerPriceAdjustment = "none" | "percentage" | "fixed_price";

/**
 * Structured pricing evidence stored in an offer line's metadata. The line's
 * `unit_amount_minor` always remains the agreed amount used for totals, Stripe
 * provisioning and the signed snapshot; this record explains how it relates to
 * the reference/list price.
 */
export interface CustomerPriceTerms {
  source: CustomerPriceSource;
  reference_unit_amount_minor: number;
  adjustment: CustomerPriceAdjustment;
  discount_bps: number;
  agreed_unit_amount_minor: number;
  note?: string;
  reference_offer_id?: string;
}

const customerPriceSource = (value: unknown): CustomerPriceSource | null =>
  typeof value === "string" && (CUSTOMER_PRICE_SOURCES as readonly string[]).includes(value)
    ? (value as CustomerPriceSource)
    : null;

const finiteMinor = (value: unknown, fallback: number): number => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : fallback;
};

/** Read structured customer pricing, falling back to the line's current amount. */
export function customerPriceTerms(line: EngineLineDraft): CustomerPriceTerms {
  const raw =
    line.metadata?.["customer_pricing"] &&
    typeof line.metadata["customer_pricing"] === "object" &&
    !Array.isArray(line.metadata["customer_pricing"])
      ? (line.metadata["customer_pricing"] as Record<string, unknown>)
      : {};
  const reference = finiteMinor(raw["reference_unit_amount_minor"], line.unit_amount_minor);
  const agreed = finiteMinor(raw["agreed_unit_amount_minor"], line.unit_amount_minor);
  const adjustment =
    raw["adjustment"] === "percentage" || raw["adjustment"] === "fixed_price"
      ? raw["adjustment"]
      : "none";
  const discountBps = Math.min(10_000, Math.max(0, finiteMinor(raw["discount_bps"], 0)));
  return {
    source:
      customerPriceSource(raw["source"]) ??
      (line.metadata?.["backfilled"] ? "stripe_subscription" : "current_catalog"),
    reference_unit_amount_minor: reference,
    adjustment,
    discount_bps: discountBps,
    agreed_unit_amount_minor: agreed,
    ...(typeof raw["note"] === "string" && raw["note"].trim() ? { note: raw["note"].trim() } : {}),
    ...(typeof raw["reference_offer_id"] === "string" && raw["reference_offer_id"].trim()
      ? { reference_offer_id: raw["reference_offer_id"].trim() }
      : {}),
  };
}

/** Apply an explicit customer price while preserving the reference/list amount. */
export function withCustomerPriceTerms(
  line: EngineLineDraft,
  input: {
    source: CustomerPriceSource;
    referenceUnitAmountMinor: number;
    adjustment: CustomerPriceAdjustment;
    discountBps?: number;
    agreedUnitAmountMinor?: number;
    note?: string;
    referenceOfferId?: string;
  },
): EngineLineDraft {
  const reference = finiteMinor(input.referenceUnitAmountMinor, line.unit_amount_minor);
  const discountBps = Math.min(10_000, Math.max(0, Math.round(input.discountBps ?? 0)));
  const agreed =
    input.adjustment === "percentage"
      ? Math.round((reference * (10_000 - discountBps)) / 10_000)
      : input.adjustment === "fixed_price"
        ? finiteMinor(input.agreedUnitAmountMinor, line.unit_amount_minor)
        : reference;
  const pricing: CustomerPriceTerms = {
    source: input.source,
    reference_unit_amount_minor: reference,
    adjustment: input.adjustment,
    discount_bps: input.adjustment === "percentage" ? discountBps : 0,
    agreed_unit_amount_minor: agreed,
    ...(input.note?.trim() ? { note: input.note.trim() } : {}),
    ...(input.referenceOfferId?.trim() ? { reference_offer_id: input.referenceOfferId.trim() } : {}),
  };
  return {
    ...line,
    unit_amount_minor: agreed,
    metadata: { ...(line.metadata ?? {}), customer_pricing: pricing },
  };
}

const toMinor = (amount: number): number => Math.round(amount * 100);

/** Build a Live plan line (or a Scout plan + optional Scenario Validation). */
export function liveLine(input: {
  apps: AppCode[];
  volumeIndex: number;
  interval: Interval;
  currency: Currency;
  planKey: LivePlanResult["key"] | ScoutPlanResult["key"];
}): EngineLineDraft | null {
  const q = liveQuote({ apps: input.apps, volumeIndex: input.volumeIndex, interval: input.interval, currency: input.currency });
  if (q.mode === "scout" && q.scout) {
    const sp = q.scout.plans.find((p) => p.key === input.planKey);
    if (!sp) return null;
    return {
      family: "live",
      plan_key: sp.key,
      display_name: `Live · ${sp.name}`,
      line_subtitle: "Lookout · forecasting",
      unit_amount_minor: toMinor(sp.monthly),
      currency_at_quote: input.currency,
      interval_at_quote: "annual", // Scout is annual-only
      apps: ["lookout"],
      product_code: "lookout",
    };
  }
  const p = q.plans.find((pl) => pl.key === input.planKey);
  if (!p || p.monthly === null) return null;
  return {
    family: "live",
    plan_key: p.key,
    display_name: `Live · ${p.name}`,
    line_subtitle: `${p.apps.map((a) => APP_NAMES[a]).join(" · ")} · ${liveCapacityLabel(p.key, input.volumeIndex)}`,
    unit_amount_minor: toMinor(p.monthly),
    currency_at_quote: input.currency,
    interval_at_quote: input.interval,
    volume_index: input.volumeIndex,
    apps: p.apps,
    product_code: p.apps[0] ?? "lookout",
  };
}

/** One-time Scenario Validation engagement (Scout-mode only). */
export function liveScoutScenarioValidationLine(currency: Currency): EngineLineDraft {
  const sv = active.live.scenarioValidation;
  return {
    family: "live",
    plan_key: "scout_scenario_validation",
    display_name: `Live · ${sv.title}`,
    line_subtitle: sv.subtitle,
    unit_amount_minor: toMinor(sv[currency]),
    currency_at_quote: currency,
    interval_at_quote: "annual",
    is_one_time: true,
    product_code: "lookout",
  };
}

/** Build a Prisma plan line. Returns null if the requested key is unavailable
 * at the chosen interval (Essentials is annual-only). */
export function prismaLine(input: {
  planKey: PrismaPlanResult["key"];
  currency: Currency;
  interval: Interval;
}): EngineLineDraft | null {
  const plans = prismaQuote(input.currency, input.interval);
  const p = plans.find((x) => x.key === input.planKey);
  if (!p || p.monthly === null) return null;
  return {
    family: "prisma",
    plan_key: p.key,
    display_name: `Prisma · ${p.name}`,
    line_subtitle: p.subtitle,
    unit_amount_minor: toMinor(p.monthly),
    currency_at_quote: input.currency,
    interval_at_quote: input.interval,
    product_code: "wave",
  };
}

/** Build an Affinity base-plan line (annual term). The metered overage is
 * tracked separately at billing time — at quote time it's reference info. */
export function affinityLine(input: {
  planKey: AffinityPlanResult["key"];
  currency: Currency;
}): EngineLineDraft | null {
  const q = affinityQuote(input.currency);
  const p = q.plans.find((x) => x.key === input.planKey);
  if (!p) return null;
  return {
    family: "affinity",
    plan_key: p.key,
    display_name: `Affinity · ${p.name}`,
    line_subtitle: `${p.includedRequests.toLocaleString("en-US")} requests/mo included`,
    unit_amount_minor: toMinor(p.monthly),
    currency_at_quote: input.currency,
    interval_at_quote: "annual",
    product_code: "affinity_api",
    metadata: {
      included_requests: p.includedRequests,
      overage_bands: q.volumeBands,
    },
  };
}

/** Optional Affinity data-integration add-on. */
export function affinityDataIntegrationLine(currency: Currency): EngineLineDraft {
  const addon = activeDataIntegrationAddon();
  return {
    family: "affinity",
    plan_key: "data_integration",
    display_name: `Affinity · ${addon.name}`,
    line_subtitle: addon.subtitle,
    unit_amount_minor: toMinor(addon[currency]),
    currency_at_quote: currency,
    interval_at_quote: "annual",
    product_code: "affinity_api",
  };
}

/** Build one canonical add-on line. User add-ons recur with their parent
 * contract; credit packages are one-time prepaid top-ups. */
export function addOnLine(input: {
  key: AddOnKey;
  currency: Currency;
  interval: Interval;
  quantity?: number;
  credits?: number;
}): EngineLineDraft | null {
  const quote = addOnQuote(input.currency);
  if (input.key === CREDIT_PACKAGE_ADDON_KEY) {
    const stepIndex = quote.credits.steps.findIndex((step) => step.credits === input.credits);
    const step = quote.credits.steps[stepIndex];
    if (!step) return null;
    return {
      family: "prisma",
      plan_key: CREDIT_PACKAGE_ADDON_KEY,
      display_name: quote.credits.name,
      line_subtitle: `${step.credits.toLocaleString("en-US")} credits · one-time`,
      unit_amount_minor: toMinor(step.amount),
      currency_at_quote: input.currency,
      interval_at_quote: input.interval,
      volume_index: stepIndex,
      is_one_time: true,
      quantity: 1,
      product_code: "wave",
      metadata: {
        addon: true,
        addon_kind: "credits",
        billing: "one_time",
        credits: step.credits,
        price_per_1000: step.perThousand,
      },
    };
  }
  const addon = quote.users.find((candidate) => candidate.key === input.key);
  const quantity = Math.max(1, Math.floor(input.quantity ?? 1));
  if (!addon) return null;
  return {
    family: "prisma",
    plan_key: addon.key,
    display_name: addon.name,
    line_subtitle: `${quantity.toLocaleString("en-US")} additional ${quantity === 1 ? "user" : "users"} for Prisma ${addon.basePlanKey === "coreplus" ? "Core+" : "Core"}`,
    unit_amount_minor: toMinor(addon.monthlyPerUser),
    currency_at_quote: input.currency,
    interval_at_quote: input.interval,
    quantity,
    product_code: "wave",
    metadata: {
      addon: true,
      addon_kind: "users",
      base_plan_key: addon.basePlanKey,
    },
  };
}

/** Keep the payable cadence aligned with the offer summary. Monthly-equivalent
 * amounts (including negotiated prices) and one-time charges stay unchanged. */
export function withOfferBillingInterval(lines: EngineLineDraft[], interval: Interval): EngineLineDraft[] {
  return lines.map((line) => line.is_one_time ? line : { ...line, interval_at_quote: interval });
}

/** Total a list of lines at the chosen interval. Recurring lines bill 12×/3×
 * monthly (annual/quarterly); one-time lines are charged once up-front. */
export function lineGrandTotalMinor(lines: EngineLineDraft[], interval: Interval): number {
  const periods = interval === "annual" ? 12 : 3;
  let total = 0;
  for (const l of lines) {
    const qty = l.quantity ?? 1;
    if (l.is_one_time) total += l.unit_amount_minor * qty;
    else total += l.unit_amount_minor * qty * periods;
  }
  return total;
}

// Note: prospect quotes are now built line-by-line from the engine builders
// above (the customer self-serve builder and the counter-propose flow share the
// same configurator). The old template "re-price a seed line from a config"
// path was removed along with the quote-template system.

// ── Product-link seed (URL → configurator) ───────────────────────────────────
// A prospect can land on the self-serve builder from a website/marketing link
// that names a PRODUCT (a subscription — "Live Core", "Live Pro 360", …), and
// the builder opens on that product. Apps (Lookout/Wave/Backhaul/Sentinel) are
// NOT part of the link: they're only a selector for *which* product, so when the
// engine needs the app set (Live plan availability / scout mode) we derive it
// from the chosen product here. Everything stays editable after the seed.

/** Forecasting-only Live tiers — Lookout-only, so they run the "scout" path. */
const LIVE_SCOUT_KEYS = ["scout", "scoutPro", "scoutEnt"] as const;
/** Every Live subscription key that a link may name (standard + scout). */
export const LIVE_PLAN_KEYS = [...LIVE_KEYS, ...LIVE_SCOUT_KEYS] as const;
export const PRISMA_PLAN_KEYS = ["essentials", "core", "coreplus"] as const;
export const AFFINITY_PLAN_KEYS = ["core", "pro"] as const;

/** The canonical app set a Live product implies — derived from the catalog, or
 * Lookout-only for the scout forecasting tiers. `[]` for unknown keys. */
export function appsForLivePlan(planKey: string): AppCode[] {
  if ((LIVE_SCOUT_KEYS as readonly string[]).includes(planKey)) return ["lookout"];
  const def = (active.live as Record<string, { apps?: string[] }>)[planKey];
  return (def?.apps as AppCode[] | undefined) ?? [];
}

/** Smallest Live volume band that covers the requested annual ticket capacity,
 * clamped to the top band; junk/≤0 falls back to the first band. */
export function volumeIndexForEvents(events: number): number {
  if (!Number.isFinite(events) || events <= 0) return 0;
  return VOLUME_TIER_ORDER.find((i) => events <= VOLUME_TIERS[i]!) ?? VOLUME_TIER_ORDER.at(-1)!;
}

/**
 * Whether a product can actually be billed quarterly at the given config.
 * Affinity and the Scout forecasting tiers are annual-only; Live/Prisma plans
 * depend on the catalogue (and Live also on the volume band). Mirrors the engine
 * line builders, so a link never seeds a quarterly interval the product can't be
 * submitted at — the server rejects an annual-only line in a quarterly offer with
 * `interval_mismatch`.
 */
export function productSupportsQuarterly(
  family: Family,
  planKey: string,
  currency: Currency = "EUR",
  volumeIndex = 0,
): boolean {
  if (family === "affinity") return false; // annual base + metered overage
  if (family === "prisma") {
    const p = prismaQuote(currency, "quarterly").find((x) => x.key === planKey);
    return !!p && !p.annualOnly && p.monthly !== null;
  }
  // Live
  if ((LIVE_SCOUT_KEYS as readonly string[]).includes(planKey)) return false; // Scout is annual-only
  const q = liveQuote({ apps: appsForLivePlan(planKey), volumeIndex, interval: "quarterly", currency });
  return q.mode === "standard" && q.plans.some((p) => p.key === planKey && p.monthly !== null);
}

/** The configurator's prefillable fields — a product selection plus offer-level
 * knobs. Structurally the web builder's `QuoteSeed`; lives here so the URL→seed
 * mapping is unit-testable and the product→apps derivation sits next to the
 * catalog. */
export interface ProductSeed {
  family?: Family;
  currency?: Currency;
  interval?: Interval;
  apps?: AppCode[];
  volumeIndex?: number;
  livePlanKey?: string;
  prismaPlanKey?: (typeof PRISMA_PLAN_KEYS)[number];
  affinityPlanKey?: (typeof AFFINITY_PLAN_KEYS)[number];
}

function asFamily(raw: string | null | undefined): Family | undefined {
  return raw === "live" || raw === "prisma" || raw === "affinity" ? raw : undefined;
}

/** Case-insensitively resolve a URL-supplied plan token to its canonical
 * (catalog-cased) key — so `live-progrowth` and `plan=proGrowth` both land on
 * `proGrowth`. Returns undefined for anything not in `keys`. */
function canonicalKey<K extends string>(keys: readonly K[], raw: string | null | undefined): K | undefined {
  if (!raw) return undefined;
  const lc = raw.toLowerCase();
  return keys.find((k) => k.toLowerCase() === lc);
}

/**
 * Resolve a configurator seed from URL params. `get` is any key→value accessor
 * (e.g. `URLSearchParams.get`), so this stays DOM-free and testable.
 *
 * The product can be named two ways (slug wins):
 *   - `?product=<family>-<plan>`   e.g. `live-pro360`, `prisma-coreplus`, `affinity-pro`
 *   - `:family` path / `?family=` + `?plan=`   e.g. `/quote/live?plan=core`
 *
 * Offer-level knobs: `?currency=EUR|USD`, `?interval=annual|quarterly`,
 * `?volume=<annual ticket capacity>` (mapped to the covering band) or `?vi=<index>`.
 *
 * Every field is independently validated; anything unknown is dropped so a stale
 * or malformed link degrades to defaults rather than breaking the page.
 */
export function productSeedFromParams(
  get: (key: string) => string | null | undefined,
  familyPathParam?: string,
): ProductSeed {
  // 1. Product → (family, plan). A `product` slug wins; else family from the
  //    path/query and plan from `?plan=`.
  let family: Family | undefined;
  let plan: string | undefined;
  const slug = get("product")?.trim().toLowerCase();
  if (slug) {
    const dash = slug.indexOf("-");
    const fam = asFamily(dash === -1 ? slug : slug.slice(0, dash));
    if (fam) {
      family = fam;
      plan = dash === -1 ? undefined : slug.slice(dash + 1);
    }
  }
  if (!family) {
    family = asFamily(familyPathParam) ?? asFamily(get("family")?.trim().toLowerCase());
    plan = get("plan")?.trim();
  }

  // 2. Offer-level knobs.
  const cur = get("currency")?.toUpperCase();
  const currency: Currency | undefined = cur === "EUR" || cur === "USD" ? cur : undefined;
  const intv = get("interval")?.toLowerCase();
  const interval: Interval | undefined = intv === "annual" || intv === "quarterly" ? intv : undefined;

  // 3. Volume band: a raw `vi` index wins, else map a human `volume`.
  let volumeIndex: number | undefined;
  const vi = get("vi");
  const vol = get("volume");
  if (vi && /^\d+$/.test(vi)) {
    const i = Number(vi);
    if (i >= 0 && i < VOLUME_TIERS.length) volumeIndex = i;
  } else if (vol && /^\d+$/.test(vol)) {
    volumeIndex = volumeIndexForEvents(Number(vol));
  }

  const seed: ProductSeed = { family, currency, interval, volumeIndex };

  // 4. Map the product onto the family-specific plan key (case-insensitive);
  //    for Live, derive the app set the product implies.
  if (family === "live") {
    const key = canonicalKey(LIVE_PLAN_KEYS, plan);
    if (key) {
      seed.livePlanKey = key;
      // Only Pro 360 has a new 25k band. Existing product links keep their
      // previous 50k configuration when given a small audience size.
      if (key !== "pro360" && seed.volumeIndex === LIVE_PRO360_25K_INDEX) seed.volumeIndex = 0;
      const apps = appsForLivePlan(key);
      if (apps.length) seed.apps = apps;
    }
  } else if (family === "prisma") {
    const key = canonicalKey(PRISMA_PLAN_KEYS, plan);
    if (key) seed.prismaPlanKey = key;
  } else if (family === "affinity") {
    const key = canonicalKey(AFFINITY_PLAN_KEYS, plan);
    if (key) seed.affinityPlanKey = key;
  }

  // 5. An annual-only product (Affinity, Scout, or a plan/band with no quarterly
  //    price) can't be billed quarterly — normalize so the link never preloads a
  //    combination the builder would reject on submit (`interval_mismatch`). A
  //    bare family with no named product keeps the requested interval.
  const resolvedKey = seed.livePlanKey ?? seed.prismaPlanKey ?? seed.affinityPlanKey;
  if (
    seed.interval === "quarterly" &&
    family &&
    resolvedKey &&
    !productSupportsQuarterly(family, resolvedKey, seed.currency, seed.volumeIndex)
  ) {
    seed.interval = "annual";
  }

  return seed;
}

/** One product a self-serve `/quote` link can encode. */
export interface QuoteLinkSelection {
  family: Family;
  /** Catalogue plan key, e.g. "core", "pro360", "coreplus", "pro". */
  planKey: string;
  currency?: Currency;
  interval?: Interval;
  /** Live only — the stable volume band index (0–12; 12 is the 25k band). */
  volumeIndex?: number;
}

/**
 * Build the `/quote` query string that prefills the self-serve builder for one
 * product — the inverse of {@link productSeedFromParams}. Lets the internal
 * offer builder turn a configured line into a shareable customer link.
 *
 * Returns "" when the selection isn't a self-serve catalogue product (custom,
 * add-on or one-time line — `plan_key` like "custom" / "data_integration"), so
 * a caller can scan an offer's lines and take the first non-empty result.
 * Origin-agnostic: the caller prepends the customer app origin.
 */
export function quoteLinkParams(sel: QuoteLinkSelection): string {
  const keys =
    sel.family === "live"
      ? LIVE_PLAN_KEYS
      : sel.family === "prisma"
        ? PRISMA_PLAN_KEYS
        : AFFINITY_PLAN_KEYS;
  if (!canonicalKey(keys, sel.planKey)) return "";

  const p = new URLSearchParams();
  p.set("product", `${sel.family}-${sel.planKey}`);
  if (sel.currency) p.set("currency", sel.currency);
  // Never emit an interval the product can't be billed at — a quarterly link for
  // an annual-only product round-trips into a seed the builder rejects on submit.
  if (
    sel.interval &&
    (sel.interval !== "quarterly" ||
      productSupportsQuarterly(sel.family, sel.planKey, sel.currency, sel.volumeIndex))
  ) {
    p.set("interval", sel.interval);
  }
  // Band 0 is the builder's default, so omit it to keep the link clean.
  if (sel.family === "live" && typeof sel.volumeIndex === "number" && sel.volumeIndex > 0) {
    p.set("vi", String(sel.volumeIndex));
  }
  return p.toString();
}

// ── Account analytics ──────────────────────────────────────────────────────
// Reporting taxonomy (tier/family) + EUR normalization for the internal
// revenue/renewal views. See ./analytics.ts.
export * from "./analytics";

// ── Client engagement ───────────────────────────────────────────────────────
// Pure activity classification + status/score derivation for the engagement KPI
// stack (analytics aggregate, client list, client detail). See ./engagement.ts.
export * from "./engagement";

// ── Catalog source-of-truth (de)serialization ───────────────────────────────
// The bridge between the published catalog snapshot (this website-catalog.json
// shape) and the normalized D1 catalog_* rows FD Billing now owns. See ./catalog.ts.
export * from "./catalog";
export * from "./entitlements";

// ── Commercial-terms footnotes (excl-VAT note + plan ad-buy fees) ────────────
// Pure resolver shared by the offer snapshot freeze and the admin builder. See
// ./commercial-terms.ts.
export * from "./commercial-terms";

// ── German display overlays ─────────────────────────────────────────────────
// Structural-key DE copy shared by the customer app's render overlays and the
// public catalog feed's `i18n.de` section (marketing-website pricing rebuild).
// Canonical strings stay English; these never feed persisted values. See ./locales/de.ts.
export * from "./locales/de";
